/**
 * Modal disparado ao arrastar um card para a lane "Recusadas/Canceladas".
 * Coleta a ação (Recusar × Cancelar) + motivo antes de chamar o backend —
 * mesmas regras da máquina de estados: recusar só de ABERTA e exige motivo;
 * cancelar de ABERTA/AUTORIZADA com motivo opcional.
 */
import React, { useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import AvisoPerdaProducao, { etapasDoErro } from "./AvisoPerdaProducao";
import SlideOverPanel from "../../../components/SlideOverPanel";

const Grupo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Opcoes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const OpcaoBtn = styled.button`
  min-height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-2)")};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
`;

const Area = styled.textarea`
  width: 100%;
  min-height: 92px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  font-size: 14px;
  resize: vertical;
  &:focus { outline: none; border-color: var(--accent); }
`;

const ErroTxt = styled.div`
  font-size: 12.5px;
  color: var(--danger-text);
`;

const Rodape = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelarBtn = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

export default function EncerrarSolicitacaoModal({ solicitacao, onConfirmar, onClose }) {
  const podeRecusar = solicitacao.situacao === "ABERTA";
  const [acao, setAcao] = useState(podeRecusar ? "recusar" : "cancelar");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState("");
  const [perda, setPerda] = useState(null);
  const [perdaConfirmada, setPerdaConfirmada] = useState(false);

  const recusar = acao === "recusar";

  const confirmar = async () => {
    if (recusar && !motivo.trim()) {
      setErro("Informe o motivo da recusa.");
      return;
    }
    if (!recusar && perda && !perdaConfirmada) {
      setErro("Marque a confirmação da perda para cancelar.");
      return;
    }
    setErro("");
    try {
      await onConfirmar(acao, motivo.trim(), { confirmarPerda: perdaConfirmada });
    } catch (e) {
      const etapas = etapasDoErro(e);
      if (!etapas) throw e;
      setPerda(etapas);
    }
  };

  const footer = (
    <Rodape>
      <CancelarBtn type="button" onClick={onClose}>
        Voltar
      </CancelarBtn>
      <AsyncButton
        variant={recusar ? "danger" : "primary"}
        silentError
        onClick={confirmar}
      >
        {recusar ? "Recusar solicitação" : "Cancelar solicitação"}
      </AsyncButton>
    </Rodape>
  );

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={`Encerrar ${solicitacao.numero}`}
      subtitle={
        podeRecusar
          ? "Recusar ou cancelar esta solicitação."
          : "Cancelar esta solicitação autorizada."
      }
      footer={footer}
    >
      <Grupo>
        {podeRecusar && (
          <Opcoes>
            <OpcaoBtn
              type="button"
              $ativo={recusar}
              onClick={() => setAcao("recusar")}
            >
              Recusar
            </OpcaoBtn>
            <OpcaoBtn
              type="button"
              $ativo={!recusar}
              onClick={() => setAcao("cancelar")}
            >
              Cancelar
            </OpcaoBtn>
          </Opcoes>
        )}
        <Label>
          {recusar
            ? "Motivo da recusa (obrigatório)"
            : "Motivo do cancelamento (opcional)"}
        </Label>
        <Area
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={
            recusar ? "Ex.: peça fora do escopo da garantia" : "Ex.: duplicada"
          }
          autoFocus
        />
        {erro && <ErroTxt>{erro}</ErroTxt>}
        {!recusar && (
          <AvisoPerdaProducao etapas={perda} confirmado={perdaConfirmada} onConfirmado={setPerdaConfirmada} />
        )}
      </Grupo>
    </SlideOverPanel>
  );
}
